import React from "react";
import { Card, Stack, Badge, Modal, Button } from "react-bootstrap";
import { cpkBadgeVariant, fmtNum, hasStatAlert, formattedTime } from "../../Services/processo/dashboardProcessoUtils";
import EstatisticasFrequenciaDashboard from "./EstatisticasFrequenciaDashboard";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell
} from "recharts";
const EstatisticaControleCard = ({
    nomePlanoSelecionado,
    current,

}) => {


    /**
     * Modal com histograma de incidências por hora (0–23)
     * histograma: Array<{ hora: number|string, incidencias: number }>
     */
    function TimeHistogramModal({ histograma = [], analiseFrequencia }) {

        const [show, setShow] = React.useState(false);
        const freqPlan = analiseFrequencia?.frequenciaPlanejada;

        // util: aceita Map<number, number> ou objeto { [hora]: incidencias }
        function toData24(hist) {
            const base = Array.from({ length: 24 }, (_, h) => ({ hora: h, incidencias: 0 }));

            if (hist instanceof Map) {
                for (const [k, v] of hist) {
                    const h = Number(k);
                    if (Number.isInteger(h) && h >= 0 && h <= 23) {
                        base[h].incidencias += Number(v) || 0;
                    }
                }
                return base;
            }

            // fallback para objeto plano
            if (hist && typeof hist === "object") {
                for (const [k, v] of Object.entries(hist)) {
                    const h = Number(k);
                    if (Number.isInteger(h) && h >= 0 && h <= 23) {
                        base[h].incidencias += Number(v) || 0;
                    }
                }
            }
            return base;
        }

        const ticks = React.useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
        const data24 = React.useMemo(() => toData24(histograma), [histograma]);

        return (
            <>
                <Button variant="primary" onClick={() => setShow(true)}>
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <rect x="3" y="10" width="3" height="11" rx="1"></rect>
                        <rect x="10" y="6" width="3" height="15" rx="1"></rect>
                        <rect x="17" y="2" width="3" height="19" rx="1"></rect>
                    </svg>{" "}
                    Frequencia
                </Button>

                <Modal
                    show={show}
                    onHide={() => setShow(false)}
                    size="lg"
                    centered
                    aria-labelledby="time-histogram-title"
                    unmountOnExit
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="time-histogram-title">
                            <Stack direction="horizontal" gap={3} className="align-items-center">
                                <span>Histograma de incidências (0–23h)</span>
                                {freqPlan > 0 && (
                                    <Badge bg="info" className="fs-6 fw-normal">
                                        Planejado: {formattedTime(freqPlan)}
                                    </Badge>
                                )}
                            </Stack>
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div style={{ width: "100%", height: 340 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={data24}
                                    margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="hora"
                                        type="number"
                                        domain={[0, 23]}
                                        ticks={ticks}
                                        allowDecimals={false}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip
                                        formatter={(value) => [value, "Incidências"]}
                                        labelFormatter={(label) => `Hora: ${label}`}
                                        cursor={{ fill: 'rgba(0,0,0,0.05)' }} 
                                    />
                                    <Bar dataKey="incidencias" radius={[4, 4, 0, 0]}>
                                        {data24.map((entry, index) => (
                                             <Cell key={`cell-${index}`} fill="#0d6efd" />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShow(false)}>
                            Fechar
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }


    return (
        <Card className="mb-3">
            <Card.Header>
                <Stack
                    direction="horizontal"
                    className="justify-content-between flex-wrap gap-2"
                >
                    <div className="d-flex flex-column">
                        <span className="text-muted small text-uppercase fw-bold">
                            {current.controle?.operacao || "Operação Geral"}
                        </span>
                        <span className="fs-5 fw-bold">
                            {current.controle?.nomeParametro || "Parâmetro"}
                            <span className="text-muted fw-normal fs-6 ms-2">
                                (ID: {current.controleId})
                            </span>
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        {TimeHistogramModal(current)}
                    </div>
                </Stack>
            </Card.Header>

            <Card.Body>
                <div className="container-fluid p-0">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-4 col-sm-6">
                            <div className="text-muted small">Performance Processo</div>
                            <h5 className="mb-1">
                                <Badge bg={cpkBadgeVariant(current.estatisticas?.cpk)}>
                                    Cpk {fmtNum(current.estatisticas?.cpk, 3)}
                                </Badge>
                            </h5>
                            <div className="small text-muted d-flex gap-2 flex-wrap">
                                <span>Méd: <b>{fmtNum(current.estatisticas?.media, 2)}</b></span>
                                <span>Min: <b>{fmtNum(current.estatisticas?.menorValor, 2)}</b></span>
                                <span>Max: <b>{fmtNum(current.estatisticas?.maiorValor, 2)}</b></span>
                            </div>
                        </div>
                        <div className="col-md-4 col-sm-6">
                            <div className="text-muted small">Desvios de Especificação</div>
                            <h5 className="mb-1">
                                <Badge bg={(current.estatisticas?.foraDeEspecificacaoTotal ?? 0) > 0 ? "danger" : "success"} title="Registros fora de especificação">
                                    {current.estatisticas?.foraDeEspecificacaoTotal ?? 0} Reg.
                                </Badge>
                            </h5>
                            <div className="small text-muted">
                                Registros fora dos limites
                            </div>
                        </div>
                        <div className="col-md-4 col-sm-12">
                            <EstatisticasFrequenciaDashboard estatisticas={current.analiseFrequencia} />
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default EstatisticaControleCard;